import { useContext, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'
import swal from 'sweetalert2'

import {
  faBan,
  faCheckCircle,
  faGrid,
  faList,
  faListCheck,
  faTimes,
  faTimesCircle,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box, Button, Columns, DashboardModule, Grid, HoverHintWrapper, LoadingModule, PageHeader, Tools,
} from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import type { DefaultContext } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import { generateUrls } from '@hooks/useTemplatePage'
import type { BulkUpdateTemplatePages } from '@hooks/useTemplatePages'
import useTemplatePages from '@hooks/useTemplatePages'
import type { TemplatePageRequestOptions } from '@models/types'

import type { TemplatePageModel } from '@models/types'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'

import EditTemplateModal from '@modals/EditTemplatePageModal/EditTemplatePageModal'

import NoFiltersBlankState from './NoFiltersBlankState'

import BulkPermissionsModal from '@modals/BulkPermissionsModal/BulkPermissionsModal'

 import TemplatePageGridTile from '@components/TemplatePageGridTile/TemplatePageGridTile'

import type { PageContextPayload } from '../index'
import ListView from './ListView'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'select',
        style,
      },
      key: 'builder',
      label: 'Template Builder',
      values: [
        { key: 'native', label: 'Realhub' },
        { key: 'canva', label: 'Canva' },
      ],
    },
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
        label: 'Brand',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ brand_id }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'templateGroups',
        label: 'Template Group',
      },
      key: 'template_group_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brandThemes',
        label: 'Brand Theme',
      },
      key: 'brand_theme_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ brand_id }) => ({ brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
        label: 'Agency',
      },
      key: 'agency_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'services',
        label: 'Service',
      },
      key: 'service_id',
    },
    {
      callbacks: {
        // @ts-expect-error TS7031
        buildSearchRequestOptions: ({ service_id }) => ({ service_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'serviceProducts',
        label: 'Product',
      },
      key: 'service_product_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
        label: 'Provider',
      },
      key: 'provider_id',
    },
    {
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
        label: 'Publication',
      },
      key: 'publication_id',
    },
  ]
}

interface PageContentProps {
  pageFilters: {
    [key: string]: any,
  },
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const hasSelectedFilters = Object.keys(pageFilters).some(key => key !== 'hidden')

  const { hidden } = parsePermittedQueryParams(getQueryParamsFromHash(), ['hidden'])
  const isHidden = hidden?.toString() ?? 'false'

  const templatePagesPayload = useTemplatePages({
    filters: {
      ...pageFilters,
      hidden: isHidden === 'true',
    },
    performHttpRequests: !!hasSelectedFilters,
    requestOptions: {
      include_template_page_dimensions: true,
      include_template_page_permission_ids: true,
      include_template_page_preview: true,
      include_template_page_in_use_count: true,
    },
  })

  const {
    callbacks: {
      bulkUpdateTemplatePages,
      loadMore,
    },
    canLoadMore,
    filteredTemplatePages,
    filteredTemplatePagesCount,
    hasFilteredTemplatePages,
    loading,
  } = templatePagesPayload

  // @ts-expect-error TS2345
  const pageContext = useContext<DefaultContext<PageContextPayload>>(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { templatePagesIndexUrl, archivedTemplatePagesIndexUrl } = generateUrls()

  const [selectedTemplatePage, setSelectedTemplatePage] = useState<null | TemplatePageModel>(null)

  const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid')
  const [selectMode, setSelectMode] = useState(false)

  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [bulkSelectPermissionsOpen, setBulkSelectPermissionsOpen] = useState(false)

  const toggleSelected = (id: number) => {
    setSelectedIds(prev => (
      prev.includes(id) ? prev.filter(selectedId => selectedId !== id) : [...prev, id]
    ))
  }

  const allVisibleSelected = !!filteredTemplatePages.length
    && filteredTemplatePages.every(templatePage => selectedIds.includes(templatePage.id))

  const selectAllVisible = () => {
    setSelectMode(true)
    setSelectedIds(prev => Array.from(new Set([...prev, ...filteredTemplatePages.map(templatePage => templatePage.id)])))
  }

const deselectAllVisible = () => {
  const visibleIds = new Set(filteredTemplatePages.map(templatePage => templatePage.id))
  setSelectedIds(prev => prev.filter(id => !visibleIds.has(id)))
}

  const toggleSelectMode = () => {
    setSelectMode(prev => {
      const next = !prev

      if (!next){
        setSelectedIds([])
      }

      return next
    })
  }

  const archiveSelectedTemplatePagesPayload = {
    callbacks: {
      action: (entityParams: BulkUpdateTemplatePages, entityRequestOptions?: TemplatePageRequestOptions) => (
        bulkUpdateTemplatePages(entityParams, entityRequestOptions)
      ),
      afterAction: () => { setSelectedIds([]); setSelectMode(false); },
      buildActionParams: () => ([{
        templatePageIds: selectedIds,
        templatePageParams: {
          hidden: true,
        },
      }]),
    },
    toastText: 'Templates Archived',
  }

  const unarchiveSelectedTemplatePagesPayload = {
    callbacks: {
      action: (entityParams: BulkUpdateTemplatePages, entityRequestOptions?: TemplatePageRequestOptions) => (
        bulkUpdateTemplatePages(entityParams, entityRequestOptions)
      ),
      afterAction: () => {setSelectedIds([]); setSelectMode(false);},
      buildActionParams: () => ([{
        templatePageIds: selectedIds,
        templatePageParams: {
          hidden: false,
        },
      }]),
    },
    toastText: 'Templates Unarchived',
  }

  const approveSelectedTemplatePagesPayload = {
    callbacks: {
      action: (entityParams: BulkUpdateTemplatePages, entityRequestOptions?: TemplatePageRequestOptions) => (
        bulkUpdateTemplatePages(entityParams, entityRequestOptions)
      ),
      afterAction: () => {setSelectedIds([]); setSelectMode(false);},
      buildActionParams: () => ([{
        templatePageIds: selectedIds,
        templatePageParams: {
          production_ready: true,
        },
      }]),
    },
    toastText: 'Templates Approved for Production',
  }

  const unapproveSelectedTemplatePagesPayload = {
    callbacks: {
      action: (entityParams: BulkUpdateTemplatePages, entityRequestOptions?: TemplatePageRequestOptions) => (
        bulkUpdateTemplatePages(entityParams, entityRequestOptions)
      ),
      afterAction: () => {setSelectedIds([]); setSelectMode(false);},
      buildActionParams: () => ([{
        templatePageIds: selectedIds,
        templatePageParams: {
          production_ready: false,
        },
      }]),
    },
    toastText: 'Templates Unapproved for Production',
  }

  const confirmBulkTemplatePageAction = (params: {
    confirmButtonColor: string,
    confirmButtonText: string,
    payload: HandleCallbackActionParams,
    title: string,
  }) => {
    const {
      confirmButtonColor,
      confirmButtonText,
      payload,
      title,
    } = params

    swal
      .fire({
        title,
        text: 'Are you sure?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText,
        confirmButtonColor,
      })
      .then(({ value }) => {
        if (value){
          handleCallbackAction(payload)
        }
      })
  }

  const updateFiltersAndClearSelectedIds: typeof updateFilters = (...args) => {
    setSelectedIds([])
    setSelectMode(false)

    return updateFilters(...args)
  }


  return (
    <>
      <Helmet>
        <title>Templates | Template Manager</title>
      </Helmet>

      <PageHeader
        activeTabBarItemKey={isHidden}
        boxProps={{ height: [112, 105], justifyContent: 'flex-start' }}
        tabBarItems={[
          {
            href: templatePagesIndexUrl,
            icon: faCheckCircle,
            key: 'false',
            title: 'Current',
          },
          {
            href: archivedTemplatePagesIndexUrl,
            icon: faBan,
            key: 'true',
            title: 'Archived',
          },
        ]}
        title="Templates"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasSelectedFilters && <NoFiltersBlankState />}

            {!loading && !hasFilteredTemplatePages && hasSelectedFilters && <ListBlankState />}

            {!loading && hasFilteredTemplatePages && (
              <DashboardModule
                contentBoxProps={{ flexDirection: 'column' }}
                headerRight={!!selectedIds.length && (

                  pageFilters?.brand_id === undefined ? (
                    <HoverHintWrapper
                      boxProps={{ display: 'inline-block', position: 'relative', width: '100%' }}
                      hintComponent={(
                        <HoverHintWrapper.Hint>
                          Use the brand filters to select multiple
                          <br />
                           templates and assign permissions at once.
                        </HoverHintWrapper.Hint>
                      )}
                    >
                      <Button
                        buttonStyle="primaryEdit"
                        disabled
                        icon={<FontAwesomeIcon icon={faListCheck} />}
                        size="small"
                        width="auto"
                      >
                        Assign Entities
                      </Button>
                    </HoverHintWrapper>
                  ) : (
                    <Button
                      buttonStyle="primaryEdit"
                      icon={<FontAwesomeIcon icon={faListCheck} />}
                      onClick={() => { setBulkSelectPermissionsOpen(true) }}
                      size="small"
                      width="auto"
                    >
                      Assign Entities
                    </Button>
                  )
                )}
                title="Template Pages"
              >
                <Box flexDirection="column">
                  { viewMode === 'grid' && (
                    <Grid
                      // @ts-expect-error TS2322
                      gridGap="medium"
                      gridAutoRows="auto"
                      gridTemplateColumns="repeat(auto-fill, minmax(240px, 1fr))"
                      padding="large"
                    >
                      {filteredTemplatePages.map((templatePage: TemplatePageModel) => (
                        <TemplatePageGridTile
                          key={templatePage.id}
                          onToggleSelected={selectMode ? toggleSelected : undefined}
                          templatePage={templatePage}
                          setSelectedTemplatePage={setSelectedTemplatePage}
                          selectedId={selectedIds.includes(templatePage.id)}
                        />
                      ))}
                    </Grid>
                  )}

                  {viewMode === 'list' && (
                    <ListView
                      selectMode={selectMode}
                      setSelectedTemplatePage={setSelectedTemplatePage}
                      templatePages={filteredTemplatePages}
                      toggleSelected={toggleSelected}
                      selectedIds={selectedIds}
                    />
                  )}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredTemplatePagesCount}
                  loading={loading}
                >
                </DashboardModule.LoadMoreFooter>
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <Tools>
              <Button
                buttonStyle="secondaryUtility"
                icon={<FontAwesomeIcon icon={viewMode === 'list' ? faGrid : faList} />}
                onClick={viewMode === 'list' ?  () => setViewMode('grid') : () => setViewMode('list')}
                size="medium"
              >
                {viewMode === 'list' ? 'View as Grid' : 'View as List'}
              </Button>


              {!!filteredTemplatePages.length  && (
                <>
                  <Button
                    buttonStyle={selectMode ? 'primaryEdit' : 'secondaryUtility'}
                    marginTop="medium"
                    icon={<FontAwesomeIcon icon={selectMode ? faTimes : faListCheck} />}
                    onClick={() => { toggleSelectMode(); setSelectedIds([]); }}
                    size="medium"
                  >
                    Select Mode
                  </Button>

                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={allVisibleSelected ? faTimesCircle : faCheckCircle} />}
                    marginBottom="medium"
                    marginTop="medium"
                    onClick={() => (allVisibleSelected ? deselectAllVisible() : selectAllVisible())}
                    size="medium"
                  >
                    {allVisibleSelected ? 'Deselect All' : 'Select All'}
                  </Button>
                </>
               )}

            {!!selectedIds.length && selectMode && isHidden === 'false' && (
              <Button
                buttonStyle="secondaryUtility"
                marginBottom="medium"
                onClick={() => {
                  confirmBulkTemplatePageAction({
                    confirmButtonColor: '#DD6B55',
                    confirmButtonText: 'Yes, archive them.',
                    // @ts-expect-error
                    payload: archiveSelectedTemplatePagesPayload,
                    title: 'Archive Selected Templates?',
                  })
                }}
                size="medium"
              >
                Archive
              </Button>
            )}

            {!!selectedIds.length && selectMode && isHidden === 'true' && (
              <Button
                buttonStyle="secondaryUtility"
                marginBottom="medium"
                onClick={() => {
                  confirmBulkTemplatePageAction({
                    confirmButtonColor: '#43ac6a',
                    confirmButtonText: 'Yes, unarchive them.',
                    // @ts-expect-error
                    payload: unarchiveSelectedTemplatePagesPayload,
                    title: 'Unarchive Selected Templates?',
                  })
                }}
                size="medium"
              >
                Unarchive
              </Button>
            )}

            {!!selectedIds.length && (
              <Button
                buttonStyle="secondaryUtility"
                marginBottom="medium"
                onClick={() => {
                  confirmBulkTemplatePageAction({
                    confirmButtonColor: '#43ac6a',
                    confirmButtonText: 'Yes, approve them.',
                    // @ts-expect-error
                    payload: approveSelectedTemplatePagesPayload,
                    title: 'Approve Selected Templates for Production?',
                  })
                }}
                size="medium"
              >
                Approve for Production
              </Button>
            )}

            {!!selectedIds.length && (
              <Button
                buttonStyle="secondaryUtility"
                marginBottom="medium"
                onClick={() => {
                  confirmBulkTemplatePageAction({
                    confirmButtonColor: '#DD6B55',
                    confirmButtonText: 'Yes, unapprove them.',
                    // @ts-expect-error
                    payload: unapproveSelectedTemplatePagesPayload,
                    title: 'Unapprove Selected Templates for Production?',
                  })
                }}
                size="medium"
              >
                Unapprove for Production
              </Button>
            )}
            </Tools>

            <PageFilterModule
              callbacks={{ resetFilters, updateFilters: updateFiltersAndClearSelectedIds }}
              defaultFilters={defaultFilters}
              // @ts-expect-error TS2322
              filterFields={buildPageFilterFields()}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>

          {selectedTemplatePage !== null && (
            <EditTemplateModal
              showModal
              modalKey="EditTemplateModal"
              templatePage={selectedTemplatePage}
              callbacks={{
                closeModal: () => { setSelectedTemplatePage(null) },
                editTemplatePage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
              }}
            />
          )}

          {bulkSelectPermissionsOpen && (
            <BulkPermissionsModal
              modalKey="BulkPermissionsModal"
              selectedTemplateIds={selectedIds}
              showModal
              callbacks={{
                closeModal: () => {setBulkSelectPermissionsOpen(false)}
              }}
            />
          )}
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
